/* eslint-disable @typescript-eslint/no-explicit-any */
import {EGender, EGenderLabel} from '@app/checkout/enums/gender.enum';
import {Order} from '@app/checkout/models/order-request.model';
import {IOrderItem, IOrderItemAttribute, OrderResponseWithSample} from '@app/checkout/models/order-response.model';
import {ISampleType} from '@app/checkout/models/sample-type.model';
import {DateUtils} from '@app/core/utils/date.util';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {StringUtils} from '@app/core/utils/string.utils';
import {IDynamicFormFieldType} from '@app/shared/models/dynamic.model';
import {DynamicFormValuesUtils} from '@app/shared/utils/dynamic-form-values.utils';
import {IEditOrderState} from '../models/my-order-state.model';

export class OrderUtils {
  static readonly firstIndex = 0;

  static getEditOrderInfo(editOrderInfo: OrderResponseWithSample, sampleTypes: ISampleType[]): IEditOrderState {
    return {
      created: editOrderInfo.created,
      confirmed: editOrderInfo.lastModified,
      testName: editOrderInfo.orderItems[0]?.name,
      days: DateUtils.getDaysBetweenDates(new Date(), new Date(editOrderInfo.created)),
      confirmDays: DateUtils.getDaysBetweenDates(new Date(), new Date(editOrderInfo.lastModified)),
      barcode: editOrderInfo.sample.barcode,
      statusId: editOrderInfo.orderStatus.id,
      status: editOrderInfo.orderStatus.name,
      tests: OrderUtils.getTestName(editOrderInfo),
      patient: OrderUtils.getPatientName(editOrderInfo),
      dob: editOrderInfo.patientDateOfBirth,
      sample: OrderUtils.getSampleName(editOrderInfo, sampleTypes),
      gender: OrderUtils.getGenderString(editOrderInfo.patientGender),
      email: editOrderInfo.patientEmail,
      phone: OrderUtils.getPatientPhoneNumber(editOrderInfo),
      id: editOrderInfo.patientId?.toString(),
      collectionDate: editOrderInfo.sample?.collectionDate,
      relatedPatients: editOrderInfo.relatedPatientsList,
      customerName: `${editOrderInfo.customerFirstName} ${editOrderInfo.customerLastName}`,
      customerEmail: `${editOrderInfo.customerEmail}`,
      customerId: `${editOrderInfo.customerId}`,
      clinicalHistory: OrderUtils.getClinicalHistory(editOrderInfo),
      noGenes: OrderUtils.getGeneCount(editOrderInfo),
      geneContent: OrderUtils.getGeneContent(editOrderInfo),
      patientMedicalRecordNumber: editOrderInfo.patientMedicalRecordNumber,
      institutionId: editOrderInfo.organization?.instituteId,
      customerDoctorId: editOrderInfo.customerDoctorId,
      institutionName: editOrderInfo.institutionName,
    };
  }

  static getPatientPhoneNumber(editOrderInfo: Order): string {
    let phone = '';
    if (editOrderInfo.patientPrefixPhoneNumber || editOrderInfo.patientPhoneNumber) {
      phone = editOrderInfo.patientPrefixPhoneNumber + editOrderInfo.patientPhoneNumber;
    }
    return phone;
  }

  static getGenderString(gender: string): string {
    switch (gender) {
      case EGender.Male:
        return EGenderLabel.Male;
      case EGender.Female:
        return EGenderLabel.Female;
      case EGender.Other:
        return EGenderLabel.Other;
      case EGender.Unknown:
        return EGenderLabel.Unknown;
      default:
        return '';
    }
  }

  static getOrderItem(editOrderInfo: Order): IOrderItem {
    return editOrderInfo?.orderItems?.filter((item, index) => index === OrderUtils.firstIndex)[OrderUtils.firstIndex];
  }

  static getSampleItem(editOrderInfo: Order): IOrderItemAttribute {
    return OrderUtils.getOrderItem(editOrderInfo).orderItemAttributes?.filter(
      (item, index) => index === OrderUtils.firstIndex
    )[OrderUtils.firstIndex];
  }

  static getTestName(editOrderInfo: Order): string {
    return OrderUtils.getSampleItem(editOrderInfo).name;
  }

  static getPatientName(editOrderInfo: Order): string {
    return editOrderInfo.patientFirstName + ' ' + editOrderInfo.patientLastName;
  }

  static getSampleName(editOrderInfo: Order, sampleTypes: ISampleType[]): string {
    return OrderUtils.getSampleInfo(editOrderInfo, sampleTypes)?.label ?? '';
  }

  static parseClinicalHistoryValues(editOrderInfo: Order) {
    const clinicalHistoryValues = OrderUtils.getOrderItem(editOrderInfo)?.clinicalHistoryValues;
    if (clinicalHistoryValues) {
      return StringUtils.parseJsonString(clinicalHistoryValues);
    }
    return [];
  }

  static parseClinicalHistoryTemplate(editOrderInfo: Order) {
    const clinicalHistoryTemplate = OrderUtils.getOrderItem(editOrderInfo)?.clinicalHistoryTemplate;
    if (clinicalHistoryTemplate) {
      return StringUtils.parseJsonString(clinicalHistoryTemplate);
    }
    return [];
  }

  static getClinicalHistory(editOrderInfo: Order): IDynamicFormFieldType[] {
    const clinicalHistoryValues = OrderUtils.getOrderItem(editOrderInfo)?.clinicalHistoryValues;
    const clinicalHistoryTemplate = OrderUtils.parseClinicalHistoryTemplate(editOrderInfo);
    if (!StringUtils.isEmpty(clinicalHistoryValues) && !ObjectUtils.isEmpty(clinicalHistoryTemplate)) {
      DynamicFormValuesUtils.setTemplateValues(clinicalHistoryValues, clinicalHistoryTemplate);
      return clinicalHistoryTemplate;
    }
    return [];
  }

  static getSampleInfo(editOrderInfo: Order, sampleTypes: ISampleType[]) {
    return sampleTypes.find(sampleType => sampleType.id === editOrderInfo.patientSampleTypeId);
  }

  static getGeneCount(editOrderInfo: Order): number {
    return OrderUtils.getSampleItem(editOrderInfo).noGenes ?? 0;
  }
  static getGeneContent(editOrderInfo: Order): string {
    return OrderUtils.getSampleItem(editOrderInfo).geneContent ?? '';
  }
}
